import React, { memo, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Divider,
    IconButton,
    Autocomplete,
    TextField,
} from "@mui/material";
import CustomLabelForm from "components/Fileds/CustomLabelForm";
import CustomTextField from "components/Fileds/CustomTextFiled";
import CustomButton from "components/Fileds/CustomButton";
import CustomMultilineField from "components/Fileds/CustomTextArea";
import MainContainer from "theme/MainConatiner";
// import DropzoneComponentPrivew from "components/DropZone/DropzoneComponent";
import CustomPhoneContryCodeInput from "components/Fileds/CustomPhoneContryCodeInput";
import CustomSelect from "components/Fileds/CustomSelect";
import { countries } from "utils/countries";
import { currencyOptions } from "utils/constant";
import DropzoneWithPreview from "components/DropZone/DropzoneComponentProfile";
import { useJsApiLoader } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import ProfileSkeleton from "./components/ProfileSkeleton";

const libraries = ['places'];

const ProfileContainer = memo(
    ({
        formik,
        initialImage,
        initialBannerImage,
        isLoading = false,
        isUpdating = false,
        handleFileUpload,
        selectedCategoryNames,
    }) => {
        const {
            values,
            touched,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched
        } = formik;

        const { isLoaded } = useJsApiLoader({
            id: 'google-map-script',
            googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyDl33GnWibNPt7vrbnU1tBFNwQ4V98aChc",
            libraries,
        });

        const {
            ready,
            value: placeValue,
            suggestions: { status, data },
            setValue: setPlaceValue,
            clearSuggestions,
            init,
        } = usePlacesAutocomplete({
            initOnMount: false,
            debounce: 300,
        });

        useEffect(() => {
            if (isLoaded) {
                init();
            }
        }, [isLoaded, init]);

        const handleSelectLocation = async (newValue) => {
            console.log("Google Places Autocomplete Selected Value:", newValue);

            if (!newValue) {
                setFieldValue("title", "");
                return;
            }

            const val = typeof newValue === 'string' ? newValue : newValue.description;
            setFieldValue("title", val);
            setPlaceValue(val, false);
            clearSuggestions();

            try {
                const results = await getGeocode({ address: val });
                if (results && results.length > 0) {
                    const { lat, lng } = await getLatLng(results[0]);
                    console.log("Full Location Response Data:", results[0]);
                    console.log("Address Components Data:", results[0].address_components);
                    console.log("Coordinates:", { lat, lng });

                    setFieldValue("lat", lat.toString());
                    setFieldValue("long", lng.toString());

                    let street_number = '';
                    let route = '';
                    let city = '';
                    let state = '';
                    let countryName = '';
                    let postal_code = '';
                    let area_name = '';

                    results[0].address_components.forEach(component => {
                        const types = component.types;
                        if (types.includes('street_number')) {
                            street_number = component.long_name;
                        }
                        if (types.includes('route')) {
                            route = component.long_name;
                        }
                        if (types.includes('locality') || types.includes("postal_town")) {
                            city = component.long_name;
                        }
                        if (types.includes('administrative_area_level_1')) {
                            state = component.long_name;
                        }
                        if (types.includes('country')) {
                            countryName = component.long_name;
                        }
                        if (types.includes('postal_code')) {
                            postal_code = component.long_name;
                        }
                        if (types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) {
                            area_name = component.long_name;
                        }
                    });

                    console.log("Extracted Area Name (Sublocality/Neighborhood):", area_name);

                    const addressLine1 = [street_number, route].filter(Boolean).join(' ');
                    if (addressLine1) setFieldValue("address", addressLine1);
                    if (city) setFieldValue("city", city);
                    if (state) setFieldValue("state", state);
                    if (postal_code) setFieldValue("postal_code", postal_code);

                    if (countryName) {
                        const countryObj = countries.find(c => c.label.toLowerCase() === countryName.toLowerCase() || c.code.toLowerCase() === countryName.toLowerCase());
                        if (countryObj) {
                            setFieldValue("country", countryObj.label);
                            setFieldValue("country_code", countryObj.country_code);
                            setFieldValue("country_ISO", countryObj.code);
                        } else {
                            setFieldValue("country", countryName);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching geocode: ", error);
            }
        };

        if (isLoading) {
            return (
                <MainContainer bgcolor={"#fff"}>
                    <ProfileSkeleton />
                </MainContainer>
            );
        }

        return (
            <>
                <MainContainer bgcolor={"#fff"}>
                    <Box className="profile-container" sx={{ p: 4, maxWidth: "1200px", margin: "0 auto" }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                {/* Images Section */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                                        Profile Image
                                    </Typography>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            height: "250px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "2px dashed #ccc",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        <Box sx={{ position: "relative" }}></Box>
                                        <DropzoneWithPreview
                                            type="image"
                                            handleFileUpload={handleFileUpload}
                                            initialImage={initialImage}
                                        />
                                        <Box
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography variant="caption" color="textSecondary">
                                                Recommended ration is 1:1
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Supported formats: PNG, JPG, and JPEG. Maximum file size: 10MB.
                                            </Typography>
                                        </Box>
                                        {touched.image && errors.image && (
                                            <Typography variant="caption" color="error">
                                                {errors.image}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>


                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                                        Banner Image
                                    </Typography>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            height: "250px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "2px dashed #ccc",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        <DropzoneWithPreview
                                            type="banner_image"
                                            handleFileUpload={handleFileUpload}
                                            initialImage={initialBannerImage}
                                        />
                                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5 }}>
                                            Supported formats: PNG, JPG, JPEG. Max file size: 10MB.
                                        </Typography>
                                    </Paper>
                                </Grid>

                                {/* Restaurant Info */}
                                <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                                    <CustomLabelForm variant="body2" label="Restaurant Name" />
                                    <CustomTextField
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.name && errors.name}
                                        helperText={touched.name && errors.name}
                                        placeholder="Enter Restaurant Name"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                                    <CustomLabelForm variant="body2" label="Restaurant Tagline" />
                                    <CustomTextField
                                        name="tagline"
                                        value={values.tagline}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.tagline && errors.tagline}
                                        helperText={touched.tagline && errors.tagline}
                                        placeholder="Enter Restaurant Tagline"
                                        inputProps={{ maxLength: 30 }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Categories" />
                                    {/* <CustomAutocomplete
                                        name="category_id"
                                        multiple
                                        value={selectedType}
                                        onChange={handleTypeChange}
                                        onBlur={handleBlur}
                                        error={touched.category_id && Boolean(errors.category_id)}
                                        helperText={touched.category_id && errors.category_id}
                                        placeholder="Select Categories"
                                        options={typeOptions}
                                        disableCloseOnSelect
                                    /> */}
                                    <CustomTextField
                                        value={selectedCategoryNames}
                                        placeholder="Categories"
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Restaurant Website" />
                                    <CustomTextField
                                        name="restaurant_website"
                                        value={values.restaurant_website}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.restaurant_website && errors.restaurant_website}
                                        helperText={touched.restaurant_website && errors.restaurant_website}
                                        placeholder="Enter Restaurant Website"
                                        type="url"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Email" />
                                    <CustomTextField
                                        name="email"
                                        value={values?.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && errors.email}
                                        helperText={touched.email && errors.email}
                                        placeholder="Enter Email Address"
                                        type="email"
                                        disabled={true}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Currency" />
                                    <CustomSelect
                                        name="currency"
                                        value={values.currency}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Select Currency"
                                        options={currencyOptions}
                                        error={touched.currency && errors.currency}
                                        helperText={touched.currency && errors.currency}
                                        disabled={true}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Service Tax (%)" />
                                    <CustomTextField
                                        name="service_tax"
                                        value={values.service_tax}
                                        onChange={(e) => {
                                            if (/^\d*\.?\d*$/.test(e.target.value)) handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.service_tax && errors.service_tax}
                                        helperText={touched.service_tax && errors.service_tax}
                                        placeholder="Enter Service Tax (%)"
                                    />
                                </Grid>

                                {/* <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Platform Fee (%)" />
                                    <CustomTextField
                                        name="platform_fee"
                                        value={values.platform_fee}
                                        onChange={(e) => {
                                            if (/^\d*\.?\d*$/.test(e.target.value)) handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.platform_fee && errors.platform_fee}
                                        helperText={touched.platform_fee && errors.platform_fee}
                                        placeholder="Enter Platform Fee (%)"
                                        disabled={true}
                                    />
                                </Grid> */}

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="VAT Number" />
                                    <CustomTextField
                                        name="vat_number"
                                        value={values.vat_number}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.vat_number && errors.vat_number}
                                        helperText={touched.vat_number && errors.vat_number}
                                        placeholder="Enter VAT Number"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Restaurant Bio" />
                                    <CustomMultilineField
                                        name="description"
                                        showError={touched.description && errors.description}
                                        errorText={touched.description && errors.description}
                                        value={values.description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Restaurant Bio"
                                        maxLength={250}
                                        rows={0}
                                    />
                                </Grid>

                                {/* Location Details Section */}
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                        Location Details
                                    </Typography>
                                    <Divider sx={{ mb: 1 }} />
                                </Grid>

                                <Grid item xs={12}>
                                    <CustomLabelForm variant="body2" label="Premises Name" />
                                    <Autocomplete
                                        id="places-autocomplete"
                                        options={data.map((suggestion) => ({ description: suggestion.description, place_id: suggestion.place_id }))}
                                        disabled={!ready}
                                        value={values.title ? { description: values.title } : null}
                                        filterOptions={(x) => x}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') { return option; }
                                            return option.description || '';
                                        }}
                                        onInputChange={(e, val, reason) => {
                                            setPlaceValue(val);
                                            setFieldValue("title", val);

                                            if (reason === "clear" || val.trim() === "") {
                                                setFieldValue("address", "");
                                                setFieldValue("address_line2", "");
                                                setFieldValue("city", "");
                                                setFieldValue("state", "");
                                                setFieldValue("postal_code", "");
                                                setFieldValue("lat", "");
                                                setFieldValue("long", "");
                                                setFieldValue("country", "");
                                                setFieldValue("country_code", "");
                                                setFieldValue("country_ISO", "");
                                            }
                                        }}
                                        onChange={(event, newValue) => {
                                            handleSelectLocation(newValue);
                                        }}
                                        onBlur={() => {
                                            setFieldTouched("title", true);
                                            clearSuggestions();
                                        }}
                                        freeSolo
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="title"
                                                error={touched.title && Boolean(errors.title)}
                                                helperText={touched.title && errors.title}
                                                placeholder="Enter Premises Name / Search Google Places"
                                                fullWidth
                                                variant="outlined"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        height: 40,
                                                        minHeight: 40,
                                                        borderRadius: "8px",
                                                        fontSize: "14px",
                                                        "& .MuiOutlinedInput-input": {
                                                            padding: "10px 2px",
                                                        },
                                                    },
                                                }}
                                                FormHelperTextProps={{
                                                    sx: {
                                                        marginLeft: 0,
                                                        marginRight: 0,
                                                        position: "absolute",
                                                        top: "40px",
                                                    },
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    autoComplete: "new-password",
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Address Line 1" />
                                    <CustomTextField
                                        name="address"
                                        value={values.address}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.address && errors.address}
                                        helperText={touched.address && errors.address}
                                        placeholder="Enter Address Line 1"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Address Line 2" />
                                    <CustomTextField
                                        name="address_line2"
                                        value={values.address_line2}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.address_line2 && errors.address_line2}
                                        helperText={touched.address_line2 && errors.address_line2}
                                        placeholder="Enter Address Line 2"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="City" />
                                    <CustomTextField
                                        name="city"
                                        value={values.city}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.city && errors.city}
                                        helperText={touched.city && errors.city}
                                        placeholder="Enter City"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="State" />
                                    <CustomTextField
                                        name="state"
                                        value={values.state}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.state && errors.state}
                                        helperText={touched.state && errors.state}
                                        placeholder="Enter State"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Country" />
                                    <Autocomplete
                                        options={countries}
                                        value={countries.find((c) => c.label === values.country) || null}
                                        onChange={(e, newValue) => {
                                            formik.setFieldValue("country", newValue ? newValue.label : "");
                                            formik.setFieldValue("country_code", newValue ? newValue.country_code : "");
                                            formik.setFieldValue("country_ISO", newValue ? newValue.code : "");
                                        }}
                                        getOptionLabel={(o) => o.label}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={touched.country && Boolean(errors.country)}
                                                helperText={touched.country && errors.country}
                                                placeholder="Select Country"
                                                variant="outlined"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        height: 40,
                                                        borderRadius: "8px",
                                                        fontSize: "14px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Postal Code" />
                                    <CustomTextField
                                        name="postal_code"
                                        value={values.postal_code}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.postal_code && errors.postal_code}
                                        helperText={touched.postal_code && errors.postal_code}
                                        placeholder="Enter Postal Code"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Latitude" />
                                    <CustomTextField
                                        name="lat"
                                        value={values.lat}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.lat && errors.lat}
                                        helperText={touched.lat && errors.lat}
                                        placeholder="Enter Latitude"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Longitude" />
                                    <CustomTextField
                                        name="long"
                                        value={values.long}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.long && errors.long}
                                        helperText={touched.long && errors.long}
                                        placeholder="Enter Longitude"
                                    />
                                </Grid>

                                {/* Contact Details Section */}
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                        Contact Details
                                    </Typography>
                                    <Divider sx={{ mb: 1 }} />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Full Name" />
                                    <CustomTextField
                                        name="full_name"
                                        value={values.full_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.full_name && errors.full_name}
                                        helperText={touched.full_name && errors.full_name}
                                        placeholder="Enter Full Name"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Email" />
                                    <CustomTextField
                                        name="personal_email"
                                        value={values.personal_email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.personal_email && errors.personal_email}
                                        helperText={touched.personal_email && errors.personal_email}
                                        placeholder="Enter Contact Email"
                                        type="email"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Contact Number" />
                                    <CustomPhoneContryCodeInput
                                        value={values.phone}
                                        countryCode={values?.country_code?.toString().replace(/^\+/, "")}
                                        country_ISO={values?.country_ISO}
                                        placeholder="Enter Contact Number"
                                        error={Boolean(touched.phone && errors.phone)}
                                        helperText={touched.phone && errors.phone}
                                        onChange={(e) => {
                                            formik.setFieldValue("phone", e.target.value.replace(/\D/g, ""))
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <CustomLabelForm variant="body2" label="Alternate Contact Number" />
                                    <CustomPhoneContryCodeInput
                                        value={values.alternate_contact_number}
                                        countryCode={values?.country_code?.toString().replace(/^\+/, "")}
                                        country_ISO={values?.country_ISO}
                                        placeholder="Enter Alternate Contact Number"
                                        error={Boolean(touched.alternate_contact_number && errors.alternate_contact_number)}
                                        helperText={touched.alternate_contact_number && errors.alternate_contact_number}
                                        onChange={(e) => {
                                            formik.setFieldValue("alternate_contact_number", e.target.value.replace(/\D/g, ""))
                                        }}
                                    />
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12} sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                                    <CustomButton
                                        type="submit"
                                        height="48px"
                                        width="200px"
                                        loading={isSubmitting || isUpdating}
                                    >
                                        Save Changes
                                    </CustomButton>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                </MainContainer>
            </>
        );
    }
);

ProfileContainer.propTypes = {
    formik: PropTypes.object.isRequired,
    initialImage: PropTypes.string,
    initialBannerImage: PropTypes.string,
    isLoading: PropTypes.bool,
    typeOptions: PropTypes.array,
    selectedType: PropTypes.array,
    handleTypeChange: PropTypes.func,
};

export default ProfileContainer;
